import {
  createAuthenticatedNoticeDisclosurePreferencesRepository,
  decodeNoticeDisclosurePreferencesProjectionV1,
  decodeNoticeDisclosurePreferenceValuesV1,
} from "./account/notice-disclosure-preferences-repository.js";
import {
  browserAccountStateSessionRepository,
} from "./account/account-state-session-repository.js";
import {
  NOTICE_DISCLOSURE_SCHEMA_VERSION,
  requireNoticeDisclosureId,
} from "./notice-disclosure-contract.js";
import {
  createBrowserNoticeDisclosurePreferenceRepository,
} from "./notice-disclosure-preference.js";

const REPOSITORY_BRAND = Symbol.for(
  "fishystuff.notice-disclosure-preference.v1",
);
const DEFAULT_ACCOUNT_VALUES = Object.freeze({
  guideTranslationHelp: null,
  calculatorDataQuality: null,
  mapZonePane: null,
});
const NOTICE_FIELD_BY_ID = Object.freeze({
  "calculator-data-quality": "calculatorDataQuality",
  "guide-translation-help": "guideTranslationHelp",
  "map-zone-pane": "mapZonePane",
});

function hasMethods(value, names) {
  return names.every((name) => typeof value?.[name] === "function");
}

function statesEqual(left, right) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function freezeDisclosures(disclosures) {
  return Object.freeze({ ...disclosures });
}

function freezeState(value) {
  const next = { ...value };
  if (next.disclosures) {
    next.disclosures = freezeDisclosures(next.disclosures);
  }
  if (next.authoritative) {
    next.authoritative = freezeState(next.authoritative);
  }
  return Object.freeze(next);
}

function disclosuresFromValues(value) {
  const values = decodeNoticeDisclosurePreferenceValuesV1(value);
  return freezeDisclosures(Object.fromEntries(
    Object.entries(NOTICE_FIELD_BY_ID)
      .filter(([, field]) => values[field] !== null)
      .map(([noticeId, field]) => [noticeId, values[field]]),
  ));
}

function projectionState(value) {
  const projection = decodeNoticeDisclosurePreferencesProjectionV1(value);
  if (projection.kind === "missing") {
    return freezeState({ kind: "missing" });
  }
  return freezeState({
    kind: "ready",
    revision: projection.revision,
    disclosures: disclosuresFromValues(projection.values),
  });
}

function boundaryState(remoteState) {
  if (remoteState?.kind === "loading") {
    return freezeState({ kind: "loading" });
  }
  if (remoteState?.kind === "uninitialized") {
    return freezeState({ kind: "uninitialized" });
  }
  if (remoteState?.kind === "locked") {
    return freezeState({
      kind: "unavailable",
      operation: "account",
      reason: remoteState.reason || "account-locked",
    });
  }
  if (remoteState?.kind === "unavailable") {
    return freezeState({
      kind: "unavailable",
      operation: remoteState.operation || "account",
      reason: remoteState.reason || "account-state-unavailable",
    });
  }
  return freezeState({
    kind: "unavailable",
    operation: "account",
    reason: "account-notice-disclosure-state-invalid",
  });
}

function pendingState(remoteState, intent) {
  const authoritative = projectionState(remoteState.authoritativeState);
  return freezeState({
    kind: "pending",
    operation: remoteState.operation,
    baseRevision: authoritative.kind === "ready"
      ? authoritative.revision
      : null,
    ...(intent
      ? {
        noticeId: intent.noticeId,
        requestedOpen: intent.open,
      }
      : {}),
    authoritative,
  });
}

function conflictState(remoteState) {
  const authoritative = projectionState(remoteState.authoritativeState);
  return freezeState({
    kind: "conflict",
    operation: remoteState.operation,
    expectedRevision: remoteState.expectedRevision,
    actualRevision: authoritative.kind === "ready"
      ? authoritative.revision
      : null,
    authoritative,
  });
}

function requireLocalRepository(repository) {
  if (
    !hasMethods(repository, [
      "dispose",
      "exportRecord",
      "load",
      "reset",
      "setOpen",
      "state",
      "subscribe",
    ])
  ) {
    throw new TypeError(
      "Notice disclosure preferences require the exact local repository contract.",
    );
  }
  return repository;
}

export function createAccountAwareNoticeDisclosurePreferenceRepository({
  localRepositoryFactory,
  authenticatedRepository,
} = {}) {
  if (typeof localRepositoryFactory !== "function") {
    throw new TypeError(
      "Notice disclosure preferences require a local repository factory.",
    );
  }
  if (
    !hasMethods(authenticatedRepository, [
      "load",
      "reset",
      "setValues",
      "state",
      "subscribe",
    ])
  ) {
    throw new TypeError(
      "Notice disclosure preferences require the authenticated repository contract.",
    );
  }

  let currentState = freezeState({ kind: "uninitialized" });
  let authority = "unknown";
  let accountProjection = null;
  let pendingIntent = null;
  let localRepository = null;
  let unsubscribeLocal = null;
  let disposed = false;
  const listeners = new Set();

  function state() {
    return currentState;
  }

  function deliver(listener) {
    try {
      listener(currentState);
    } catch (_error) {
      globalThis.reportError?.(
        new Error("Notice disclosure preference subscriber failed."),
      );
    }
  }

  function publish(nextState) {
    if (disposed) return currentState;
    const frozen = freezeState(nextState);
    if (statesEqual(currentState, frozen)) return currentState;
    currentState = frozen;
    for (const listener of listeners) deliver(listener);
    return currentState;
  }

  function releaseLocal() {
    if (!localRepository) return;
    const repository = localRepository;
    authority = "unknown";
    localRepository = null;
    unsubscribeLocal?.();
    unsubscribeLocal = null;
    repository.dispose();
  }

  function createLocalRepository() {
    return requireLocalRepository(localRepositoryFactory());
  }

  function activateLocal() {
    if (disposed || (authority === "local" && localRepository)) return state();
    releaseLocal();
    accountProjection = null;
    pendingIntent = null;
    let repository = null;
    try {
      repository = createLocalRepository();
      const loaded = repository.load();
      authority = "local";
      localRepository = repository;
      unsubscribeLocal = repository.subscribe((nextState) => {
        if (
          !disposed
          && authority === "local"
          && repository === localRepository
        ) {
          publish(nextState);
        }
      });
      return publish(loaded ?? repository.state());
    } catch (_error) {
      repository?.dispose?.();
      authority = "unknown";
      localRepository = null;
      unsubscribeLocal = null;
      return publish({
        kind: "unavailable",
        operation: "load",
        reason: "local-notice-disclosure-load-failed",
      });
    }
  }

  function projectAuthenticated(remoteState) {
    if (disposed) return;
    if (remoteState?.kind === "signedOut") {
      activateLocal();
      return;
    }

    releaseLocal();
    try {
      if (remoteState?.kind === "ready") {
        authority = "account";
        accountProjection = decodeNoticeDisclosurePreferencesProjectionV1(
          remoteState.authoritativeState,
        );
        pendingIntent = null;
        publish(projectionState(accountProjection));
        return;
      }
      if (remoteState?.kind === "pending") {
        authority = "account";
        accountProjection = decodeNoticeDisclosurePreferencesProjectionV1(
          remoteState.authoritativeState,
        );
        publish(pendingState(remoteState, pendingIntent));
        return;
      }
      if (remoteState?.kind === "conflict") {
        authority = "account";
        accountProjection = decodeNoticeDisclosurePreferencesProjectionV1(
          remoteState.authoritativeState,
        );
        pendingIntent = null;
        publish(conflictState(remoteState));
        return;
      }
      authority = "unknown";
      accountProjection = null;
      pendingIntent = null;
      publish(boundaryState(remoteState));
    } catch (_error) {
      authority = "unknown";
      accountProjection = null;
      pendingIntent = null;
      publish({
        kind: "unavailable",
        operation: "load",
        reason: "account-notice-disclosure-state-invalid",
      });
    }
  }

  const unsubscribeAuthenticated = authenticatedRepository.subscribe(
    projectAuthenticated,
  );

  async function load() {
    if (disposed) return state();
    await authenticatedRepository.load();
    return state();
  }

  async function setOpen(noticeId, open, options) {
    const exactNoticeId = requireNoticeDisclosureId(noticeId);
    if (typeof open !== "boolean") {
      throw new TypeError("Notice disclosure open command must be boolean.");
    }
    if (disposed || currentState.kind === "pending") return state();
    if (authority === "local" && localRepository) {
      await localRepository.setOpen(exactNoticeId, open, options);
      return state();
    }
    if (authority !== "account" || !accountProjection) return state();

    const field = NOTICE_FIELD_BY_ID[exactNoticeId];
    const currentValues = accountProjection.kind === "ready"
      ? accountProjection.values
      : DEFAULT_ACCOUNT_VALUES;
    const nextValues = decodeNoticeDisclosurePreferenceValuesV1({
      ...currentValues,
      [field]: open,
    });
    const intent = Object.freeze({ noticeId: exactNoticeId, open });
    pendingIntent = intent;
    try {
      await authenticatedRepository.setValues(nextValues, options);
      return state();
    } finally {
      if (pendingIntent === intent) pendingIntent = null;
    }
  }

  async function reset(options) {
    if (disposed || currentState.kind === "pending") return state();
    if (authority === "local" && localRepository) {
      await localRepository.reset(options);
      return state();
    }
    if (authority !== "account" || !accountProjection) return state();
    pendingIntent = null;
    await authenticatedRepository.reset(options);
    return state();
  }

  async function resetLocal(options) {
    if (disposed) {
      return freezeState({
        kind: "unavailable",
        operation: "reset",
        reason: "notice-disclosure-repository-disposed",
      });
    }
    if (authority === "local" && localRepository) {
      return localRepository.reset(options);
    }

    const temporary = createLocalRepository();
    try {
      return await temporary.reset(options);
    } finally {
      temporary.dispose();
    }
  }

  function subscribe(listener) {
    if (typeof listener !== "function") {
      throw new TypeError("Notice disclosure preference listener must be a function.");
    }
    if (disposed) return () => {};
    listeners.add(listener);
    deliver(listener);
    return () => listeners.delete(listener);
  }

  function dispose() {
    if (disposed) return;
    disposed = true;
    unsubscribeAuthenticated();
    releaseLocal();
    accountProjection = null;
    pendingIntent = null;
    listeners.clear();
  }

  return Object.freeze({
    [REPOSITORY_BRAND]: NOTICE_DISCLOSURE_SCHEMA_VERSION,
    dispose,
    load,
    reset,
    resetLocal,
    setOpen,
    state,
    subscribe,
  });
}

let browserOwner = null;
let browserAuthenticatedOwner = null;
let browserLocalResetAdapter = null;
let browserLocalResetWindow = null;
let browserWindow = null;

function browserWindowRef() {
  const windowRef = globalThis.window;
  if (!windowRef || typeof windowRef !== "object") {
    throw new TypeError("Browser notice disclosure preferences require a window.");
  }
  return windowRef;
}

function disposeBrowserOwner() {
  browserOwner?.dispose();
  browserAuthenticatedOwner?.dispose();
  browserOwner = null;
  browserAuthenticatedOwner = null;
}

export function browserNoticeDisclosurePreferenceRepository() {
  const windowRef = browserWindowRef();
  if (browserOwner && browserWindow !== windowRef) {
    disposeBrowserOwner();
  }
  if (!browserOwner) {
    browserAuthenticatedOwner =
      createAuthenticatedNoticeDisclosurePreferencesRepository({
        sessionRepository: browserAccountStateSessionRepository(),
        apiBaseUrl: windowRef.__fishystuffApiBaseUrl,
      });
    browserOwner = createAccountAwareNoticeDisclosurePreferenceRepository({
      localRepositoryFactory:
        createBrowserNoticeDisclosurePreferenceRepository,
      authenticatedRepository: browserAuthenticatedOwner,
    });
    browserWindow = windowRef;
    void browserOwner.load();
  }
  return browserOwner;
}

export function browserLocalNoticeDisclosurePreferenceRepository() {
  const windowRef = browserWindowRef();
  if (!browserLocalResetAdapter || browserLocalResetWindow !== windowRef) {
    browserLocalResetWindow = windowRef;
    browserLocalResetAdapter = Object.freeze({
      async reset(options) {
        if (browserOwner && browserWindow === windowRef) {
          return browserOwner.resetLocal(options);
        }
        const temporary = requireLocalRepository(
          createBrowserNoticeDisclosurePreferenceRepository(),
        );
        try {
          return await temporary.reset(options);
        } finally {
          temporary.dispose();
        }
      },
    });
  }
  return browserLocalResetAdapter;
}
